import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/http";
import { useT } from "@/i18n/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, Loader2, Sparkles, Save, ChevronDown, ChevronRight, Bell, Target } from "lucide-react";

const ACTIVITY_LEVELS = [
  { key: "sedentario", labelKey: "calc.actSedentary", detail: "×1.2" },
  { key: "leggero",    labelKey: "calc.actLight",     detail: "×1.375" },
  { key: "moderato",   labelKey: "calc.actModerate",  detail: "×1.55" },
  { key: "intenso",    labelKey: "calc.actIntense",   detail: "×1.725" },
  { key: "atleta",     labelKey: "calc.actAthlete",   detail: "×1.9" },
];

const GOALS = [
  { key: "dimagrimento", labelKey: "calc.goalCut",      detail: "-20%" },
  { key: "mantenimento", labelKey: "calc.goalMaintain", detail: "±0" },
  { key: "performance",  labelKey: "calc.goalPerf",     detail: "+5%" },
  { key: "massa",        labelKey: "calc.goalBulk",     detail: "+15%" },
];

// BMR formulas the coach can choose from. "needsBF" flags formulas that
// require a body-fat percentage to actually run — otherwise the backend
// silently falls back to Mifflin and returns `formula: "mifflin"`.
const FORMULAS = [
  { key: "mifflin",    labelKey: "nutriPlan.fMifflin",    hintKey: "nutriPlan.fMifflinHint",    needsBF: false },
  { key: "harris",     labelKey: "nutriPlan.fHarris",     hintKey: "nutriPlan.fHarrisHint",     needsBF: false },
  { key: "katch",      labelKey: "nutriPlan.fKatch",      hintKey: "nutriPlan.fKatchHint",      needsBF: true  },
  { key: "cunningham", labelKey: "nutriPlan.fCunningham", hintKey: "nutriPlan.fCunninghamHint", needsBF: true  },
];

const GOAL_PRESET_ADJ = { dimagrimento: -20, mantenimento: 0, performance: 5, massa: 15 };

const PRESETS = [
  { key: "balanced",     labelKey: "nutriPlan.presetBalanced",  p: 30, c: 40, f: 30 },
  { key: "lowcarb",      labelKey: "nutriPlan.presetLowCarb",   p: 35, c: 25, f: 40 },
  { key: "highprotein",  labelKey: "nutriPlan.presetHighProt",  p: 40, c: 35, f: 25 },
];

function ageFromBirthdate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age > 0 && age < 120 ? age : "";
}

const num = (v) => (v === "" || v === null || v === undefined ? "" : String(v));

/**
 * NutritionPlanDialog: coach-side daily-target editor for a single client.
 * Backend endpoints:
 *   GET  /nutritionist/clients/:id/nutrition-plan
 *   POST /nutritionist/clients/:id/calc-macros
 *   PUT  /nutritionist/clients/:id/nutrition-plan
 */
export default function NutritionPlanDialog({ open, onOpenChange, clientId, clientName, onSaved, selfMode = false }) {
  const [history, setHistory] = useState([]);
  const HIST_LABELS = {
    daily_calories_target: "kcal", protein_target_g: "P", carbs_target_g: "C",
    fat_target_g: "F", fiber_target_g: "Fib", water_target_l: "H₂O",
  };
  const { t } = useT();
  const [mode, setMode] = useState("auto"); // "auto" | "manual"
  const [preset, setPreset] = useState("custom");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [computing, setComputing] = useState(false);
  const [showMicros, setShowMicros] = useState(false);
  const [notifyClient, setNotifyClient] = useState(true);

  const [calc, setCalc] = useState({
    gender: "male", age: "", height_cm: "", weight_kg: "",
    activity_level: "moderato", goal: "mantenimento", adjustment_pct: 0,
    formula: "mifflin", body_fat_pct: "",
  });
  // Editable targets (grams, litres, mg, µg)
  const [tgt, setTgt] = useState({
    daily_calories_target: "", protein_target_g: "", carbs_target_g: "", fat_target_g: "",
    fiber_target_g: "", water_target_l: "",
    sodium_target_mg: "", calcium_target_mg: "", iron_target_mg: "", vit_d_target_ug: "",
  });

  // Load client profile when opened
  useEffect(() => {
    if (!open || (!clientId && !selfMode)) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(selfMode ? "/client/nutrition-plan" : `/nutritionist/clients/${clientId}/nutrition-plan`);
        const p = data.profile || {};
        setCalc((c) => ({
          ...c,
          gender: p.gender === "female" || p.gender === "F" ? "female" : "male",
          age: ageFromBirthdate(p.birthdate) || c.age,
          height_cm: p.height_cm || c.height_cm,
          weight_kg: data.latest_weight_kg || p.starting_weight_kg || c.weight_kg,
          activity_level: p.activity_level || c.activity_level,
          goal: p.goal || c.goal,
        }));
        setTgt({
          daily_calories_target: num(p.daily_calories_target),
          protein_target_g: num(p.protein_target_g),
          carbs_target_g: num(p.carbs_target_g),
          fat_target_g: num(p.fat_target_g),
          fiber_target_g: num(p.fiber_target_g),
          water_target_l: num(p.water_target_l),
          sodium_target_mg: num(p.sodium_target_mg),
          calcium_target_mg: num(p.calcium_target_mg),
          iron_target_mg: num(p.iron_target_mg),
          vit_d_target_ug: num(p.vit_d_target_ug),
        });
        setHistory(data.history || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("common.error"));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId]);

  const canCalc = useMemo(
    () => calc.age && calc.height_cm && calc.weight_kg &&
      +calc.age >= 10 && +calc.height_cm >= 100 && +calc.weight_kg >= 30,
    [calc]
  );

  const runCalc = async () => {
    if (!canCalc) { toast.error(t("calc.missing")); return; }
    const chosen = FORMULAS.find((f) => f.key === calc.formula);
    if (chosen?.needsBF) {
      const bf = parseFloat(calc.body_fat_pct);
      if (!Number.isFinite(bf) || bf < 3 || bf > 60) {
        toast.error(t("nutriPlan.needBodyFat"));
        return;
      }
    }
    setComputing(true);
    try {
      const body = {
        gender: calc.gender, age: +calc.age,
        height_cm: +calc.height_cm, weight_kg: +calc.weight_kg,
        activity_level: calc.activity_level, goal: calc.goal, apply: false,
        formula: calc.formula || "mifflin",
      };
      if (chosen?.needsBF) body.body_fat_pct = parseFloat(calc.body_fat_pct);
      const presetAdj = GOAL_PRESET_ADJ[calc.goal] ?? 0;
      if (+calc.adjustment_pct !== presetAdj) body.adjustment_pct = +calc.adjustment_pct;
      if (preset !== "custom") {
        const pr = PRESETS.find((p) => p.key === preset);
        if (pr) { body.protein_pct = pr.p; body.carbs_pct = pr.c; body.fat_pct = pr.f; }
      }
      const { data } = await api.post(selfMode ? "/client/calc-macros" : `/nutritionist/clients/${clientId}/calc-macros`, body);
      setTgt((s) => ({
        ...s,
        daily_calories_target: String(data.target_kcal),
        protein_target_g: String(data.protein_g),
        carbs_target_g: String(data.carbs_g),
        fat_target_g: String(data.fat_g),
        // Sensible defaults for fiber & water when auto-computing (WHO / EFSA)
        fiber_target_g: s.fiber_target_g || "30",
        water_target_l: s.water_target_l || (calc.gender === "female" ? "2" : "2.5"),
      }));
      // If the backend fell back (needsBF but body_fat_pct missing), tell the coach.
      if (data.formula_requested && data.formula && data.formula !== data.formula_requested) {
        toast.warning(t("nutriPlan.fallbackToMifflin"));
      } else {
        toast.success(t("nutriPlan.calcDone"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setComputing(false); }
  };

  const applyPreset = (key) => {
    setPreset(key);
    if (key === "custom") return;
    const kcal = parseFloat(tgt.daily_calories_target);
    if (!kcal || kcal < 500) { toast.info(t("nutriPlan.needKcalForPreset")); return; }
    const pr = PRESETS.find((p) => p.key === key);
    setTgt((s) => ({
      ...s,
      protein_target_g: String(Math.round((kcal * pr.p / 100) / 4)),
      carbs_target_g: String(Math.round((kcal * pr.c / 100) / 4)),
      fat_target_g: String(Math.round((kcal * pr.f / 100) / 9)),
    }));
  };

  const save = async () => {
    const kcal = parseInt(tgt.daily_calories_target, 10);
    if (!kcal || kcal < 800 || kcal > 6000) {
      toast.error(t("nutriPlan.invalidKcal"));
      return;
    }
    setSaving(true);
    try {
      const payload = { notify_client: notifyClient };
      const numFields = [
        "daily_calories_target","protein_target_g","carbs_target_g","fat_target_g",
        "fiber_target_g","water_target_l","sodium_target_mg","calcium_target_mg",
        "iron_target_mg","vit_d_target_ug",
      ];
      for (const k of numFields) {
        const v = tgt[k];
        if (v === "" || v === null || v === undefined) continue;
        const parsed = k === "daily_calories_target" ? parseInt(v, 10) : parseFloat(v);
        if (Number.isFinite(parsed)) payload[k] = parsed;
      }
      if (calc.goal) payload.goal = calc.goal;
      if (calc.activity_level) payload.activity_level = calc.activity_level;
      const { data } = await api.put(selfMode ? "/client/nutrition-plan" : `/nutritionist/clients/${clientId}/nutrition-plan`, payload);
      toast.success(
        !selfMode && notifyClient && data.pushed
          ? t("nutriPlan.savedPushed")
          : t("nutriPlan.saved")
      );
      if (onSaved) onSaved(data.profile);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setSaving(false); }
  };

  // Live macro breakdown percentages
  const kcal = parseFloat(tgt.daily_calories_target) || 0;
  const pctFromG = (g, kcalPerG) => (kcal > 0 && g ? Math.round((parseFloat(g) * kcalPerG / kcal) * 100) : 0);
  const pP = pctFromG(tgt.protein_target_g, 4);
  const pC = pctFromG(tgt.carbs_target_g, 4);
  const pF = pctFromG(tgt.fat_target_g, 9);
  const pSum = pP + pC + pF;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl"
        data-testid="nutrition-plan-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Target className="h-5 w-5 text-[#E8951C]" />
            {t("nutriPlan.title")}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {t("nutriPlan.subtitle")} <b>{selfMode ? t("nutriPlan.selfLabel") : (clientName || t("nutriPlan.client"))}</b>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#5A8B8F]" />
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Mode toggle */}
            <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-full">
              {[
                { key: "auto",   labelKey: "nutriPlan.modeAuto",   icon: Calculator },
                { key: "manual", labelKey: "nutriPlan.modeManual", icon: Save },
              ].map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMode(m.key)}
                  data-testid={`nutriplan-mode-${m.key}`}
                  className={`h-10 rounded-full text-sm font-medium transition inline-flex items-center justify-center gap-1.5 ${
                    mode === m.key
                      ? "bg-white dark:bg-slate-900 text-[#2C5254] dark:text-white shadow-sm"
                      : "text-slate-500"
                  }`}
                >
                  <m.icon className="h-3.5 w-3.5" /> {t(m.labelKey)}
                </button>
              ))}
            </div>

            {mode === "auto" && (
              <div className="rounded-2xl border border-[#5A8B8F]/25 bg-[#EAF3F4]/30 dark:bg-[#22383a]/40 p-4 space-y-3">
                {/* Formula picker */}
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1 mb-1.5">
                    <Sparkles className="h-3 w-3 text-[#E8951C]" /> {t("nutriPlan.formulaTitle")}
                  </p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {FORMULAS.map((f) => (
                      <button
                        key={f.key} type="button"
                        onClick={() => setCalc({ ...calc, formula: f.key })}
                        data-testid={`nutriplan-formula-${f.key}`}
                        className={`px-2.5 h-11 rounded-xl text-[11px] font-semibold border text-left flex flex-col justify-center leading-tight transition ${
                          calc.formula === f.key
                            ? "bg-[#5A8B8F] text-white border-[#5A8B8F]"
                            : "bg-white dark:bg-slate-800 text-slate-600 border-slate-200 dark:border-slate-700 hover:border-[#5A8B8F]/40"
                        }`}
                      >
                        <span className="font-bold">{t(f.labelKey)}</span>
                        <span className={`text-[9px] mt-0.5 ${calc.formula === f.key ? "text-white/80" : "text-slate-400"}`}>
                          {t(f.hintKey)}
                        </span>
                      </button>
                    ))}
                  </div>
                  {FORMULAS.find((f) => f.key === calc.formula)?.needsBF && (
                    <div className="mt-2">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                        {t("nutriPlan.bodyFat")} (%)
                      </Label>
                      <Input
                        type="number" inputMode="decimal" step="0.1" min={3} max={60}
                        value={calc.body_fat_pct}
                        onChange={(e) => setCalc({ ...calc, body_fat_pct: e.target.value })}
                        data-testid="nutriplan-bodyfat"
                        placeholder="25"
                        className="mt-1 h-10"
                      />
                      <p className="text-[10px] text-slate-400 mt-1">{t("nutriPlan.bodyFatHint")}</p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">{t("calc.gender")}</Label>
                    <div className="mt-1 grid grid-cols-2 gap-1 p-1 bg-white dark:bg-slate-800 rounded-full">
                      {["male", "female"].map((g) => (
                        <button
                          key={g} type="button"
                          onClick={() => setCalc({ ...calc, gender: g })}
                          data-testid={`nutriplan-gender-${g}`}
                          className={`h-9 rounded-full text-xs font-medium transition ${
                            calc.gender === g ? "bg-[#5A8B8F] text-white" : "text-slate-500"
                          }`}
                        >
                          {t(`calc.gender${g === "male" ? "M" : "F"}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{t("calc.age")}</Label>
                    <Input type="number" inputMode="numeric" min={10} max={100}
                      value={calc.age} onChange={(e) => setCalc({ ...calc, age: e.target.value })}
                      data-testid="nutriplan-age" placeholder="34" className="mt-1 h-10" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{t("calc.height")} (cm)</Label>
                    <Input type="number" inputMode="numeric" min={100} max={250}
                      value={calc.height_cm} onChange={(e) => setCalc({ ...calc, height_cm: e.target.value })}
                      data-testid="nutriplan-height" placeholder="175" className="mt-1 h-10" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{t("calc.weight")} (kg)</Label>
                    <Input type="number" inputMode="decimal" step="0.1" min={30} max={300}
                      value={calc.weight_kg} onChange={(e) => setCalc({ ...calc, weight_kg: e.target.value })}
                      data-testid="nutriplan-weight" placeholder="75" className="mt-1 h-10" />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-500">{t("calc.activity")}</Label>
                  <div className="mt-1 grid grid-cols-5 gap-1">
                    {ACTIVITY_LEVELS.map((a) => (
                      <button
                        key={a.key} type="button"
                        onClick={() => setCalc({ ...calc, activity_level: a.key })}
                        data-testid={`nutriplan-activity-${a.key}`}
                        className={`h-9 rounded-lg text-[11px] font-medium border transition ${
                          calc.activity_level === a.key
                            ? "bg-[#5A8B8F] text-white border-[#5A8B8F]"
                            : "bg-white dark:bg-slate-800 text-slate-600 border-slate-200 dark:border-slate-700 hover:border-[#5A8B8F]/40"
                        }`}
                      >
                        {t(a.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-500">{t("calc.goal")}</Label>
                  <div className="mt-1 grid grid-cols-4 gap-1">
                    {GOALS.map((g) => (
                      <button
                        key={g.key} type="button"
                        onClick={() => {
                          const p = GOAL_PRESET_ADJ[g.key];
                          setCalc({ ...calc, goal: g.key, adjustment_pct: p });
                        }}
                        data-testid={`nutriplan-goal-${g.key}`}
                        className={`h-10 rounded-lg text-xs font-medium border flex flex-col items-center justify-center transition ${
                          calc.goal === g.key
                            ? "bg-[#E8951C] text-white border-[#E8951C]"
                            : "bg-white dark:bg-slate-800 text-slate-600 border-slate-200 dark:border-slate-700 hover:border-[#E8951C]/40"
                        }`}
                      >
                        <span className="truncate max-w-full">{t(g.labelKey)}</span>
                        <span className={`text-[9px] ${calc.goal === g.key ? "text-white/80" : "text-slate-400"}`}>{g.detail}</span>
                      </button>
                    ))}
                  </div>

                  {/* Custom kcal adjustment slider — lets the coach override the preset -20% for cut, etc. */}
                  <div className="mt-3 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <Label className="text-xs text-slate-600 dark:text-slate-300 font-medium">
                        {t("nutriPlan.customAdj")}
                      </Label>
                      <span
                        data-testid="nutriplan-adj-value"
                        className={`text-sm font-semibold tabular-nums ${
                          +calc.adjustment_pct > 0 ? "text-emerald-600"
                          : +calc.adjustment_pct < 0 ? "text-orange-600" : "text-slate-500"
                        }`}
                      >
                        {+calc.adjustment_pct > 0 ? "+" : ""}{calc.adjustment_pct}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="-40" max="30" step="1"
                      value={calc.adjustment_pct ?? 0}
                      onChange={(e) => setCalc({ ...calc, adjustment_pct: parseInt(e.target.value, 10) })}
                      data-testid="nutriplan-adj-slider"
                      className="w-full accent-[#E8951C]"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">{t("nutriPlan.customAdjHint")}</p>
                  </div>
                </div>

                <Button
                  onClick={runCalc}
                  disabled={!canCalc || computing}
                  data-testid="nutriplan-calc-btn"
                  className="w-full rounded-full bg-[#5A8B8F] hover:bg-[#4a7679] text-white h-11"
                >
                  {computing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calculator className="h-4 w-4 mr-2" />}
                  {t("nutriPlan.calculate")}
                </Button>
              </div>
            )}

            {/* Preset ratio picker */}
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
                {t("nutriPlan.presetTitle")}
              </Label>
              <div className="mt-1 grid grid-cols-4 gap-1.5">
                {[...PRESETS, { key: "custom", labelKey: "nutriPlan.presetCustom" }].map((p) => (
                  <button
                    key={p.key} type="button"
                    onClick={() => applyPreset(p.key)}
                    data-testid={`nutriplan-preset-${p.key}`}
                    className={`px-2 h-11 rounded-xl text-[11px] font-semibold border transition text-center flex flex-col items-center justify-center leading-tight ${
                      preset === p.key
                        ? "bg-[#2C5254] text-white border-[#2C5254]"
                        : "bg-white dark:bg-slate-800 text-slate-600 border-slate-200 dark:border-slate-700 hover:border-[#2C5254]/40"
                    }`}
                  >
                    <span>{t(p.labelKey)}</span>
                    {p.p && <span className={`text-[9px] mt-0.5 ${preset === p.key ? "text-white/80" : "text-slate-400"}`}>P{p.p}/C{p.c}/F{p.f}</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Editable target values */}
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3 flex items-center gap-1.5">
                <Target className="h-3 w-3 text-[#E8951C]" /> {t("nutriPlan.targetsTitle")}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricInput
                  label="Kcal" unit="kcal" testId="tgt-kcal"
                  value={tgt.daily_calories_target}
                  onChange={(v) => setTgt({ ...tgt, daily_calories_target: v })}
                  accent="#2C5254"
                />
                <MetricInput
                  label={t("common.protein")} unit="g" testId="tgt-protein"
                  value={tgt.protein_target_g}
                  onChange={(v) => setTgt({ ...tgt, protein_target_g: v })}
                  accent="#0284c7" pct={pP}
                />
                <MetricInput
                  label={t("common.carbs")} unit="g" testId="tgt-carbs"
                  value={tgt.carbs_target_g}
                  onChange={(v) => setTgt({ ...tgt, carbs_target_g: v })}
                  accent="#a855f7" pct={pC}
                />
                <MetricInput
                  label={t("common.fat")} unit="g" testId="tgt-fat"
                  value={tgt.fat_target_g}
                  onChange={(v) => setTgt({ ...tgt, fat_target_g: v })}
                  accent="#84cc16" pct={pF}
                />
              </div>
              {pSum > 0 && Math.abs(pSum - 100) > 3 && (
                <p className="text-[11px] text-amber-600 mt-2" data-testid="tgt-macro-warn">
                  {t("nutriPlan.macroSumWarn").replace("{sum}", pSum)}
                </p>
              )}

              <div className="grid grid-cols-2 gap-3 mt-3">
                <MetricInput
                  label={t("nutriPlan.fiber")} unit="g" testId="tgt-fiber"
                  value={tgt.fiber_target_g}
                  onChange={(v) => setTgt({ ...tgt, fiber_target_g: v })}
                  accent="#059669" hint="30g"
                />
                <MetricInput
                  label={t("nutriPlan.water")} unit="L" testId="tgt-water"
                  value={tgt.water_target_l} step="0.1"
                  onChange={(v) => setTgt({ ...tgt, water_target_l: v })}
                  accent="#0ea5e9" hint="2–2.5L"
                />
              </div>
            </div>

            {/* Micronutrients (collapsible) */}
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <button
                type="button"
                onClick={() => setShowMicros((v) => !v)}
                data-testid="nutriplan-micros-toggle"
                className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
              >
                {showMicros ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex-1">
                  {t("nutriPlan.microsTitle")}
                </span>
                <span className="text-[10px] text-slate-400">{t("nutriPlan.optional")}</span>
              </button>
              {showMicros && (
                <div className="px-4 pb-4 grid grid-cols-2 gap-3">
                  <MetricInput
                    label={t("nutriPlan.sodium")} unit="mg" testId="tgt-sodium"
                    value={tgt.sodium_target_mg}
                    onChange={(v) => setTgt({ ...tgt, sodium_target_mg: v })}
                    hint="≤2000mg"
                  />
                  <MetricInput
                    label={t("nutriPlan.calcium")} unit="mg" testId="tgt-calcium"
                    value={tgt.calcium_target_mg}
                    onChange={(v) => setTgt({ ...tgt, calcium_target_mg: v })}
                    hint="1000mg"
                  />
                  <MetricInput
                    label={t("nutriPlan.iron")} unit="mg" testId="tgt-iron"
                    value={tgt.iron_target_mg} step="0.1"
                    onChange={(v) => setTgt({ ...tgt, iron_target_mg: v })}
                    hint="8–18mg"
                  />
                  <MetricInput
                    label={t("nutriPlan.vitD")} unit="µg" testId="tgt-vitd"
                    value={tgt.vit_d_target_ug} step="0.5"
                    onChange={(v) => setTgt({ ...tgt, vit_d_target_ug: v })}
                    hint="15µg"
                  />
                </div>
              )}
            </div>

            {/* Goal history — how the plan evolved over time */}
            {history.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="plan-history">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t("nutriPlan.historyTitle")}</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {history.map((h, i) => (
                    <div key={i} className="text-[11px] text-slate-600 flex items-start gap-2" data-testid={`plan-history-row-${i}`}>
                      <span className="tabular-nums text-slate-400 shrink-0">{(h.date || "").slice(0, 10)}</span>
                      <span className={`shrink-0 rounded-full px-1.5 py-0 text-[9px] font-bold ${h.changed_by === "coach" ? "bg-[#E8951C]/15 text-[#9a6410]" : "bg-[#5A8B8F]/15 text-[#2C5254]"}`}>
                        {h.changed_by === "coach" ? t("nutriPlan.byCoach") : t("nutriPlan.bySelf")}
                      </span>
                      <span className="flex-1">
                        {Object.entries(h.changes || {}).map(([k, v]) =>
                          `${HIST_LABELS[k] || k} ${v.from ?? "–"}→${v.to}`
                        ).join(" · ")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notify toggle — coach mode only */}
            {!selfMode && (
            <label
              className="flex items-center gap-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
              data-testid="nutriplan-notify-row"
            >
              <input
                type="checkbox"
                checked={notifyClient}
                onChange={(e) => setNotifyClient(e.target.checked)}
                data-testid="nutriplan-notify-checkbox"
                className="h-4 w-4 accent-[#E8951C]"
              />
              <Bell className="h-4 w-4 text-[#E8951C]" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                  {t("nutriPlan.notifyLabel")}
                </p>
                <p className="text-[11px] text-slate-500">{t("nutriPlan.notifyHint")}</p>
              </div>
            </label>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="nutriplan-cancel-btn">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={save}
            disabled={saving || loading}
            data-testid="nutriplan-save-btn"
            className="rounded-full bg-[#E8951C] hover:bg-[#c47a10] text-white flex-1 sm:flex-none"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {t("nutriPlan.saveAndActivate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetricInput({ label, unit, value, onChange, accent = "#1A1D1E", hint, step = "1", pct, testId }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1 relative">
        <Input
          type="number" inputMode="decimal" step={step} min={0}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="h-10 pr-10 font-semibold tabular-nums text-right"
          style={{ color: accent }}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-medium text-slate-400 pointer-events-none">
          {unit}
        </span>
      </div>
      {(pct != null || hint) && (
        <p className="text-[10px] text-slate-400 mt-1 tabular-nums">
          {pct != null && pct > 0 ? `${pct}% · ` : ""}{hint || ""}
        </p>
      )}
    </div>
  );
}
